// "Who did this" attribution for a ציר זמן row — an 18px avatar + a caption
// name, folded into the row's meta line next to the timestamp so it reads as
// metadata and never competes with the event itself.
//
// Costs no reads: the server resolves the actor inline on every timeline action
// (`actionUser` = MiniUser with userName + userAvatar/photoURL), so this is a
// pure render of data the row already holds.
import { Box, Stack, Avatar, Tooltip, Typography } from "@material-ui/core";
import { alpha, useTheme } from "@material-ui/core/styles";
import createAvatar from "utils/createAvatar";
import { actorFullLabel } from "./timelineFormat";
import type { TimelineEntry } from "./timelineData";

const AVATAR_SIZE = 18;

type TimelineActorProps = {
  user: TimelineEntry["user"];
  /** Precomputed short name (actorDisplayName) — "" means "no known actor". */
  name: string;
  /** Own events take the info accent, teammates' the secondary — same language as the notes tab. */
  isSelf: boolean;
};

export function TimelineActor({ user, name, isSelf }: TimelineActorProps): JSX.Element | null {
  const theme = useTheme();
  // Defensive: system / automatic entries carry no actor — render nothing at
  // all rather than a blank avatar.
  if (!name) return null;

  const accent = isSelf ? theme.palette.info : theme.palette.secondary;
  const initial = createAvatar(name).name || "?";

  return (
    <Tooltip title={actorFullLabel(user)} arrow>
      <Stack direction="row" alignItems="center" gap={0.5} sx={{ minWidth: 0 }}>
        <Avatar
          alt=""
          src={user.userAvatar || undefined}
          sx={{
            width: AVATAR_SIZE,
            height: AVATAR_SIZE,
            fontSize: 9,
            fontWeight: 700,
            flexShrink: 0,
            bgcolor: alpha(accent.main, 0.16),
            color: accent.dark
          }}
        >
          <Box component="span" aria-hidden>
            {initial}
          </Box>
        </Avatar>
        <Typography
          noWrap
          variant="caption"
          sx={{ color: "text.secondary", maxWidth: 160, lineHeight: 1.2 }}
        >
          {name}
        </Typography>
      </Stack>
    </Tooltip>
  );
}
